import { computed, toValue, type MaybeRefOrGetter } from 'vue';

import type { AdCampaign, GetAdCampaignsArgs } from '@/composables/platform/types';
import type { Brand } from '@/types/auth.types';
import type { HttpRequestConfig } from '@/types/http';

import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useFacebookStore } from '@/stores/facebook';
import { useIdentityStore } from '@/stores/identity';

interface MetaAdAccount {
  accountId: string;
  accountName: string;
  currency: string;
  brandIds: number[];
  [key: string]: unknown;
}

/**
 * The slice of the (untyped JS) facebook store this composable consumes. Declared locally so the
 * ad-account wrapper is type-checked; keep in sync with `@/stores/facebook`.
 */
interface FacebookAdsStore {
  adAccountsList?: MetaAdAccount[];
  marketingV2Campaigns: Record<string, AdCampaign> | null;
  metaAdsAccountToCurrency: Record<string, string>;
  adAccountsLoading: boolean;
  pending: { marketingV2Campaigns: boolean };
  error: { adAccounts: unknown; campaigns: unknown };
  getAdAccounts(args: { brandIds?: number[] }): Promise<unknown>;
  getMarketingV2Campaigns(
    args: { adAccountIds?: unknown[]; brandIds?: number[]; sourceCampaignIds?: unknown[] },
    axiosConfig?: HttpRequestConfig,
  ): Promise<unknown>;
}

export function useMetaAdsAccountDetails() {
  const facebookStore = useFacebookStore() as unknown as FacebookAdsStore;
  const identityStore = useIdentityStore();

  const adAccountsList = computed(() =>
    facebookStore.adAccountsList?.map((adAccount) => ({
      sourceAdAccountId: adAccount.accountId,
      name: adAccount.accountName,
      ...adAccount,
    })),
  );

  const adCampaigns = computed(() => facebookStore.marketingV2Campaigns);

  const adCampaignsList = computed(() => {
    if (!facebookStore.marketingV2Campaigns) return [];
    return Object.values(facebookStore.marketingV2Campaigns);
  });

  const adAccountToCurrency = computed(() => facebookStore.metaAdsAccountToCurrency);

  const adAccountsLoading = computed(() => facebookStore.adAccountsLoading);
  const adCampaignsLoading = computed(() => facebookStore.pending.marketingV2Campaigns);
  const adAccountsError = computed(() => facebookStore.error.adAccounts);
  const adCampaignsError = computed(() => facebookStore.error.campaigns);
  async function getAdAccounts({ brandIds }: { brandIds?: MaybeRefOrGetter<number[]> }) {
    await facebookStore.getAdAccounts({ brandIds: toValue(brandIds) });
  }

  async function getAdCampaigns(
    { brandIds, adAccountIds, sourceCampaignIds }: GetAdCampaignsArgs,
    axiosConfig?: HttpRequestConfig,
  ) {
    await facebookStore.getMarketingV2Campaigns(
      {
        adAccountIds: toValue(adAccountIds),
        brandIds: toValue(brandIds),
        sourceCampaignIds: toValue(sourceCampaignIds),
      },
      axiosConfig,
    );
  }

  function brandHasPermission(brand: Brand | null) {
    return identityStore.guard(BRAND.ADS.CAN_ACCESS_ADS, brand);
  }

  function userHasPermission(brand: Brand | null) {
    return identityStore.guard(USER.ADS.CAN_ACCESS_ADS, brand);
  }

  function userAndBrandHavePermission(brand: Brand | null) {
    return brandHasPermission(brand) && userHasPermission(brand);
  }

  return {
    adAccountsList,
    adAccountsLoading,
    adAccountsError,
    adAccountToCurrency,
    adCampaigns,
    adCampaignsList,
    adCampaignsLoading,
    adCampaignsError,
    getAdAccounts,
    getAdCampaigns,
    brandHasPermission,
    userHasPermission,
    userAndBrandHavePermission,
  };
}
